import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import {
  Send,
  ArrowLeft,
  Users,
  Search,
  Check,
  X,
  Loader2,
  MessageSquare,
  AlertCircle,
  CheckCircle,
  History,
  Filter
} from "lucide-react";

const BulkMessaging = () => {
  const { user } = useAuth();
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [jobFilter, setJobFilter] = useState("");
  const [jobs, setJobs] = useState([]);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [credits, setCredits] = useState(0);

  useEffect(() => {
    fetchCandidates();
    fetchJobs();
    fetchHistory();
    fetchCredits();
  }, []);

  const fetchCredits = async () => {
    try {
      const response = await fetch(`${API}/auth/me`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setCredits(data.outreach_credits || 0);
      }
    } catch (error) {
      console.error("Failed to fetch credits:", error);
    }
  };

  const fetchCandidates = async () => {
    try {
      const response = await fetch(`${API}/candidates/applicants/all`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        // Handle both array and object with applicants array
        if (Array.isArray(data)) {
          setCandidates(data);
        } else if (data && Array.isArray(data.applicants)) {
          setCandidates(data.applicants);
        } else {
          setCandidates([]);
        }
      } else {
        setCandidates([]);
      }
    } catch (error) {
      console.error("Failed to fetch candidates:", error);
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await fetch(`${API}/jobs/my-jobs`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setJobs(data);
      }
    } catch (error) {
      console.error("Failed to fetch jobs:", error);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await fetch(`${API}/messages/bulk-history`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setHistory(data);
      }
    } catch (error) {
      console.error("Failed to fetch history:", error);
    }
  };

  const toggleCandidate = (candidateId) => {
    setSelectedCandidates(prev => 
      prev.includes(candidateId)
        ? prev.filter(id => id !== candidateId)
        : [...prev, candidateId]
    );
  };

  const selectAll = () => {
    const filtered = getFilteredCandidates();
    if (selectedCandidates.length === filtered.length) {
      setSelectedCandidates([]);
    } else {
      setSelectedCandidates(filtered.map(c => c.candidate_id || c.user_id));
    }
  };

  const sendBulkMessage = async () => {
    if (!message.trim()) {
      toast.error("Please enter a message");
      return;
    }
    
    if (selectedCandidates.length === 0) {
      toast.error("Please select at least one candidate");
      return;
    }
    
    if (selectedCandidates.length > credits) {
      toast.error(`Not enough credits. You need ${selectedCandidates.length} but have ${credits}`);
      return;
    }
    
    setSending(true);
    
    try {
      const response = await fetch(`${API}/messages/bulk-send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          recipient_ids: selectedCandidates,
          content: message,
          job_id: jobFilter || null
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Messages sent to ${data.total_sent} candidates`);
        setMessage("");
        setSelectedCandidates([]);
        setCredits(data.credits_remaining);
        fetchHistory();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send messages");
      }
    } catch (error) {
      console.error("Failed to send bulk message:", error);
      toast.error("Failed to send messages");
    } finally {
      setSending(false);
    }
  };

  const getFilteredCandidates = () => {
    return candidates.filter(c => {
      const matchesSearch = !searchQuery || 
        (c.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (c.headline || "").toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesJob = !jobFilter || c.job_id === jobFilter;
      
      return matchesSearch && matchesJob;
    });
  };

  const filteredCandidates = getFilteredCandidates();

  return (
    <div className="min-h-screen bg-obsidian flex">
      <Sidebar />
      
      <main className="flex-1 lg:ml-64 p-4 sm:p-6 pt-20 lg:pt-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <Link to="/dashboard/recruiter" className="text-gray-400 hover:text-white">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="font-heading text-xl sm:text-2xl font-bold">BULK MESSAGING</h1>
                <p className="text-gray-400 text-xs sm:text-sm">Send messages to multiple candidates at once</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="text-right">
                <p className="text-xs sm:text-sm text-gray-400">Outreach Credits</p>
                <p className="text-lg sm:text-xl font-bold text-crimson">{credits}</p>
              </div>
              <Button
                onClick={() => setShowHistory(!showHistory)}
                variant="outline"
                className="border-[#333] text-white text-sm"
                data-testid="toggle-history-btn"
              >
                <History className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">History</span>
              </Button>
            </div>
          </div>

          {showHistory ? (
            /* History View */
            <motion.div
              className="bg-obsidian-light border border-[#222] p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h2 className="font-heading text-lg font-bold mb-4">Message History</h2>
              {history.length === 0 ? (
                <p className="text-gray-400">No bulk messages sent yet</p>
              ) : (
                <div className="space-y-3">
                  {history.map((item) => (
                    <div key={item.log_id} className="p-4 bg-obsidian border border-[#333]">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-400">
                          {new Date(item.created_at).toLocaleDateString()}
                        </span>
                        <span className="text-sm">
                          <span className="text-green-400">{item.successful}</span>
                          <span className="text-gray-400"> / {item.total_recipients} sent</span>
                        </span>
                      </div>
                      <p className="text-gray-300">{item.content_preview}...</p>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          ) : (
            /* Compose View */
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Candidate Selection */}
              <motion.div
                className="bg-obsidian-light border border-[#222] p-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading text-lg font-bold">Select Candidates</h2>
                  <span className="text-sm text-gray-400">
                    {selectedCandidates.length} selected
                  </span>
                </div>
                
                {/* Filters */}
                <div className="flex gap-3 mb-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search candidates..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-obsidian border border-[#333] text-white placeholder-gray-500 focus:border-crimson focus:outline-none"
                      data-testid="search-input"
                    />
                  </div>
                  <select
                    value={jobFilter}
                    onChange={(e) => setJobFilter(e.target.value)}
                    className="px-3 py-2 bg-obsidian border border-[#333] text-white focus:border-crimson focus:outline-none"
                    data-testid="job-filter"
                  >
                    <option value="">All Jobs</option>
                    {jobs.map((job) => (
                      <option key={job.job_id} value={job.job_id}>
                        {job.title}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Select All */}
                <button
                  onClick={selectAll}
                  className="w-full mb-3 py-2 border border-[#333] text-sm text-gray-400 hover:text-white hover:border-[#444] transition-colors"
                  data-testid="select-all-btn"
                >
                  {selectedCandidates.length === filteredCandidates.length && filteredCandidates.length > 0
                    ? "Deselect All"
                    : `Select All (${filteredCandidates.length})`}
                </button>
                
                {/* Candidate List */}
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {loading ? (
                    <div className="text-center py-8">
                      <Loader2 className="w-6 h-6 text-crimson animate-spin mx-auto" />
                    </div>
                  ) : filteredCandidates.length === 0 ? (
                    <p className="text-gray-400 text-center py-8">No candidates found</p>
                  ) : (
                    filteredCandidates.map((candidate) => {
                      const id = candidate.candidate_id || candidate.user_id;
                      const isSelected = selectedCandidates.includes(id);
                      
                      return (
                        <button
                          key={id}
                          onClick={() => toggleCandidate(id)}
                          className={`w-full flex items-center gap-3 p-3 border transition-colors ${
                            isSelected
                              ? "bg-crimson/10 border-crimson"
                              : "bg-obsidian border-[#333] hover:border-[#444]"
                          }`}
                          data-testid={`candidate-${id}`}
                        >
                          <div className={`w-5 h-5 border flex items-center justify-center ${
                            isSelected ? "bg-crimson border-crimson" : "border-[#444]"
                          }`}>
                            {isSelected && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <img
                            src={candidate.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(candidate.name || "U")}&background=FF2A2A&color=fff`}
                            alt={candidate.name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                          <div className="flex-1 text-left">
                            <p className="font-medium text-white">{candidate.name}</p>
                            <p className="text-sm text-gray-400">{candidate.headline || candidate.job_title || "Candidate"}</p>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </motion.div>
              
              {/* Message Composer */}
              <motion.div
                className="bg-obsidian-light border border-[#222] p-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <h2 className="font-heading text-lg font-bold mb-4">Compose Message</h2>
                
                {/* Credits Warning */}
                {selectedCandidates.length > credits && (
                  <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 flex items-center gap-2 text-red-400">
                    <AlertCircle className="w-5 h-5" />
                    <span className="text-sm">
                      Not enough credits. Need {selectedCandidates.length}, have {credits}.
                    </span>
                  </div>
                )}
                
                {/* Selected Count */}
                <div className="mb-4 p-3 bg-obsidian border border-[#333] flex items-center gap-2">
                  <Users className="w-5 h-5 text-crimson" />
                  <span className="text-gray-300">
                    Sending to <span className="text-white font-bold">{selectedCandidates.length}</span> candidates
                  </span>
                  <span className="ml-auto text-sm text-gray-400">
                    Cost: {selectedCandidates.length} credits
                  </span>
                </div>
                
                {/* Message Input */}
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type your message here... This message will be sent to all selected candidates."
                  rows={10}
                  className="w-full p-4 bg-obsidian border border-[#333] text-white placeholder-gray-500 resize-none focus:border-crimson focus:outline-none"
                  data-testid="message-input"
                />
                
                {/* Character Count */}
                <div className="flex items-center justify-between mt-2 mb-4">
                  <span className="text-sm text-gray-400">
                    {message.length} characters
                  </span>
                </div>
                
                {/* Send Button */}
                <Button
                  onClick={sendBulkMessage}
                  disabled={sending || selectedCandidates.length === 0 || !message.trim() || selectedCandidates.length > credits}
                  className="w-full bg-crimson hover:bg-crimson-hover disabled:opacity-50"
                  data-testid="send-bulk-btn"
                >
                  {sending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send to {selectedCandidates.length} Candidates
                    </>
                  )}
                </Button>
              </motion.div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default BulkMessaging;
